// lib/breadcrumb.ts

export interface Crumb {
  name: string;
  href: string; // absolute URL
  position: number; // 1-based for Schema.org
}

const LABELS: Record<string, string> = {
  blog: "Study Resources",
  careers: "Careers",
  "ip-mathematics-tutor": "IP Mathematics Tutor",
  "ip-physics-tutor": "IP Physics Tutor",
  "ip-chemistry-tutor": "IP Chemistry Tutor",
  "ip-biology-tutor": "IP Biology Tutor",
  "stem-lab-facilitator": "STEM Lab Facilitator",
  "stem-lab-assistant": "STEM Lab Assistant",
  "ip-physics-tuition": "IP Physics Tuition",
  "ip-maths-tuition": "IP Maths Tuition",
  "ip-chemistry-tuition": "IP Chemistry Tuition",
  "ip-biology-tuition": "IP Biology Tuition",
  "ip-lower-secondary-maths-tuition": "IP Lower Secondary Maths Tuition",
  "ip-lower-secondary-science-tuition": "IP Lower Secondary Science Tuition",
  "ip-upper-secondary-maths-tuition": "IP Upper Secondary Maths Tuition",
  "ip-upper-secondary-physics-tuition": "IP Upper Secondary Physics Tuition",
  "ip-upper-secondary-chemistry-tuition":
    "IP Upper Secondary Chemistry Tuition",
  "ip-upper-secondary-biology-tuition": "IP Upper Secondary Biology Tuition",
  "ip-combined-sciences-lower-sec-notes": "IP Combined Science Lower Sec Notes",
  "ip-maths-lower-sec-notes": "IP Maths Lower Sec Notes",
  "h2-physics-notes": "H2 Physics Notes",
  "h2-chemistry-notes": "H2 Chemistry Notes",
  "h2-biology-notes": "H2 Biology Notes",
  "ip-physics-upper-sec-notes": "IP Physics Upper Sec Notes",
  "ip-chemistry-upper-sec-notes": "IP Chemistry Upper Sec Notes",
  "ip-biology-upper-sec-notes": "IP Biology Upper Sec Notes",
  "ip-emaths-upper-sec-notes": "IP EMaths Upper Sec Notes",
  "ip-amaths-upper-sec-notes": "IP AMaths Upper Sec Notes",
  "sec-g3-mathematics-notes": "O-Level and SEC G3 Mathematics Notes",
  "sec-g3-additional-mathematics-notes":
    "O-Level and SEC G3 Additional Mathematics Notes",
  "sec-g3-physics-notes": "O-Level and SEC G3 Physics Notes",
  "sec-g3-chemistry-notes": "O-Level and SEC G3 Chemistry Notes",
  "sec-g3-biology-notes": "O-Level and SEC G3 Biology Notes",
  "sec-g3-science-physics-chemistry-notes":
    "O-Level and SEC G3 Physics Chemistry Notes",
  "sec-g3-science-physics-biology-notes":
    "O-Level and SEC G3 Physics Biology Notes",
  "sec-g3-science-chemistry-biology-notes":
    "O-Level and SEC G3 Chemistry Biology Notes",
  scholarships: "Scholarship Pathways",
  bursaries: "Bursary Profiles",
  quizzes: "Quizzes",
  "h2-chemistry": "H2 Chemistry Quizzes",
  "h2-biology": "H2 Biology Quizzes",
  "h2-physics": "H2 Physics Quizzes",
  "h2-maths": "H2 Maths Quizzes",
  "ip-chemistry-upper-sec": "IP Chemistry Quizzes",
  "ip-biology-upper-sec": "IP Biology Quizzes",
  "ip-physics-upper-sec": "IP Physics Quizzes",
  "ip-amaths-upper-sec": "IP A.Maths Quizzes",
  "ip-emaths-upper-sec": "IP E.Maths Quizzes",
  "ip-maths-lower-sec": "IP Maths Quizzes",
  "chemistry-experiments": "Chemistry Practicals (Labs & Experiments)",
  "h2-physics-experiments": "H2 Physics Practicals (Labs & Experiments)",
  "h2-chemistry-experiments": "H2 Chemistry Practicals (Labs & Experiments)",
  "h2-biology-experiments": "H2 Biology Practicals (Labs & Experiments)",
  "o-level-physics-experiments":
    "O-Level Physics Practicals (Labs & Experiments)",
  "o-level-chemistry-experiments":
    "O-Level Chemistry Practicals (Labs & Experiments)",
  "o-level-biology-experiments":
    "O-Level Biology Practicals (Labs & Experiments)",
  cc: "CC Study Rooms",
  "one-north": "one-north",
  "app-privacy": "App Privacy Policy",
  privacy: "Privacy Policy",
  terms: "Terms of Use",
  cookies: "Cookie Policy",
  // fallback: capitalise first letter
};

export function buildBreadcrumbs(pathname = "/"): Crumb[] {
  const segments = pathname
    .replace(/^\/|\/$/g, "")
    .split("/")
    .filter(Boolean);

  const crumbs: Crumb[] = [{ name: "Home", href: "/", position: 1 }];
  let position = 1;
  let href = "";
  segments.forEach((seg) => {
    href += `/${seg}`;
    position += 1;
    crumbs.push({
      name:
        LABELS[seg] ??
        seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      href,
      position,
    });
  });
  return crumbs;
}

export function breadcrumbJsonLd(crumbs: Crumb[]): string {
  const origin = process.env.SITE_URL ?? "https://eclatinstitute.sg";
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    name: "Breadcrumbs",
    itemListElement: crumbs.map((c) => ({
      "@type": "ListItem",
      position: c.position,
      name: c.name,
      /* ensure absolute */
      item: new URL(c.href, origin).href,
    })),
  }).replace(/</g, "\\u003c"); // XSS-safe for <Script dangerouslySetInnerHTML>
}
